import { useState } from 'react';

import { AppIcon } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';

import { Box, SxProps, Typography, Theme, Chip } from '@mui/material';

const EXPLANATION_STRINGS = appStrings.modules.lifeReadiness;

type Explanation = 'knowledge' | 'achievement';

const InfoRow = ({ index, title, text }: { index: number; title: string; text: string }) => {
  return (
    <Box sx={infoRowContainerSx}>
      <Typography variant="text-sm-medium" sx={numberSx}>
        {index + 1}
      </Typography>

      <Typography variant="text-sm-bold" sx={titleSx}>
        {title}
      </Typography>

      <Typography variant="text-sm-regular" sx={descriptionSx}>
        {text}
      </Typography>
    </Box>
  );
};

export const ScoreExplanationTable = () => {
  const [subcategory, setSubcategory] = useState<Explanation>('knowledge');

  const switchSubcategory = () => {
    setSubcategory(subcategory === 'knowledge' ? 'achievement' : 'knowledge');
  };

  const getExplanations = (subcategory: Explanation) => {
    return EXPLANATION_STRINGS.questionTooltips[subcategory];
  };

  const explanations = getExplanations(subcategory);

  return (
    <Box sx={containerSx}>
      <Box sx={headerContainerSx}>
        <Typography variant="text-xl-semibold" sx={subCategorySx}>
          {subcategory.charAt(0).toUpperCase() + subcategory.slice(1)}
        </Typography>

        <Box sx={headerActionSx} onClick={switchSubcategory}>
          <Chip
            variant="badge"
            size="md"
            type="leading"
            icon={
              <AppIcon
                iconName="RefreshCcw05"
                diameter="12px"
                color="Colors/Foreground/fg-quaternary"
              />
            }
            label={EXPLANATION_STRINGS.switchTo(
              subcategory === 'knowledge' ? 'achievement' : 'knowledge',
            )}
            sx={headerActionChipSx}
          />
        </Box>
      </Box>

      <Box sx={explanationsContainerSx}>
        {Object.keys(explanations).map((explanation, index) => {
          const explanationData = explanations[explanation as keyof typeof explanations];

          return (
            <InfoRow
              key={explanation}
              index={index}
              title={explanationData.title}
              text={explanationData.tooltip}
            />
          );
        })}
      </Box>
    </Box>
  );
};

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
  paddingX: backrs.spacing['spacing-xl'],
  paddingY: backrs.spacing['spacing-2xl'],
  borderRadius: backrs.radius['radius-xl'],
  height: '100%',
});

const subCategorySx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
  userSelect: 'none',
});

const explanationsContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-3xl'],
});

const infoRowContainerSx: SxProps<Theme> = ({ backrs, breakpoints }) => ({
  display: 'grid',
  gridTemplateColumns: '16px 70px 1fr',
  alignItems: 'start',
  gap: backrs.spacing['spacing-md'],
  [breakpoints.down('sm')]: {
    gridTemplateColumns: '16px 1fr',
    gridTemplateAreas: `
      "number title"
      ".      desc"
    `,
  },
});

const headerContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: backrs.spacing['spacing-lg'],
  mb: backrs.spacing['spacing-2xl'],
  flexWrap: 'wrap',
});

const headerActionSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  cursor: 'pointer',
};

const headerActionChipSx: SxProps<Theme> = ({ backrs }) => ({
  border: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
});

const numberSx: SxProps<Theme> = ({ backrs }) => ({
  gridArea: { xs: 'number', sm: 'auto' },
  color: backrs.colors['Colors/Text/text-secondary'],
  userSelect: 'none',
});

const titleSx: SxProps<Theme> = ({ backrs }) => ({
  width: 'fit-content',
  whiteSpace: 'nowrap',
  gridArea: { xs: 'title', sm: 'auto' },
  color: backrs.colors['Colors/Text/text-brand-tertiary'],
  userSelect: 'none',
});

const descriptionSx: SxProps<Theme> = ({ backrs }) => ({
  gridArea: { xs: 'desc', sm: 'auto' },
  color: backrs.colors['Colors/Text/text-secondary-hover'],
});
